import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";

/**
 * Universal HID Scanner Input Layer (Enterprise PRD · June 2026).
 *
 * External USB / Bluetooth barcode scanners act as keyboards ("keyboard
 * wedge"): they type the code as ultra-fast keystrokes followed by Enter.
 * This provider listens globally (capture phase) so NO input field needs to
 * be focused. Detection rule: ≥5 printable chars with <45 ms between
 * keystrokes, terminated by Enter.
 *
 * - Pages subscribe with `useHidScanner(onScan)` — the newest subscriber wins.
 * - If nobody is subscribed and the user is a scanning role, we stash the code
 *   and jump to /app/check-in which processes it on mount.
 * - `scannerConnected` flips true after the first detected scan (used by the
 *   check-in welcome screen's "Scanner Ready" chip).
 */
const HidCtx = createContext({ scannerConnected: false, subscribe: () => () => {} });

const SCAN_MIN_LEN = 5;
// Wedge scanners deliver keystrokes far faster than humans, but on a busy
// main thread (live camera decode) delivered keydown gaps can inflate a lot.
// Decision on Enter uses the MEDIAN inter-key gap (event.timeStamp based):
//  - keystrokes typed OUTSIDE any input field are almost certainly a scanner,
//    so we allow generous jitter (<=150 ms median);
//  - inside an editable field we require true scanner cadence (<=60 ms) so a
//    fast typist pressing Enter in a form never triggers a phantom scan.
const RESET_GAP_MS = 500;
const MEDIAN_GAP_FREE_MS = 150;
const MEDIAN_GAP_EDITABLE_MS = 60;

function isEditableTarget(t) {
  if (!t) return false;
  const tag = (t.tagName || "").toUpperCase();
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable;
}
const SCANNING_ROLES = ["staff", "agent", "support", "gate_officer", "organizer", "merchant", "business_owner", "admin"];
export const PENDING_HID_KEY = "icaard_pending_hid_scan";

export function HidScannerProvider({ children }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [scannerConnected, setScannerConnected] = useState(
    () => localStorage.getItem("icaard_hid_seen") === "1"
  );
  const subscribersRef = useRef([]);
  const bufRef = useRef({ chars: "", gaps: [], lastAt: 0 });
  const userRef = useRef(user);
  const pathRef = useRef(location.pathname);
  userRef.current = user;
  pathRef.current = location.pathname;

  const subscribe = useCallback((cb) => {
    subscribersRef.current.push(cb);
    return () => {
      subscribersRef.current = subscribersRef.current.filter((f) => f !== cb);
    };
  }, []);

  useEffect(() => {
    const deliver = (code) => {
      setScannerConnected(true);
      localStorage.setItem("icaard_hid_seen", "1");
      const subs = subscribersRef.current;
      if (subs.length > 0) {
        subs[subs.length - 1](code);
        return;
      }
      const role = userRef.current?.role;
      // Universal card QR (identical on every physical iCaard) — route by role.
      if (/\/card\/?$/.test(code) || code === "ICAARD" || code.endsWith("/card")) {
        if (role && ["merchant", "business_owner"].includes(role)) nav("/app/merchant/sell-icaard");
        else if (userRef.current) nav("/app/card");
        else nav("/card");
        return;
      }
      if (role && SCANNING_ROLES.includes(role) && pathRef.current !== "/app/check-in") {
        sessionStorage.setItem(PENDING_HID_KEY, code);
        toast.info("Scanner input detected. opening check in…");
        nav("/app/check-in");
      } else if (userRef.current) {
        toast.info(`Scanner read: ${code}`);
      }
    };

    const onKeyDown = (e) => {
      const now = e.timeStamp || Date.now();
      const b = bufRef.current;
      const gap = now - b.lastAt;
      if (gap > RESET_GAP_MS) { b.chars = ""; b.gaps = []; }
      else if (b.chars.length > 0) b.gaps.push(gap);
      b.lastAt = now;
      if (e.key === "Enter") {
        const gaps = [...b.gaps].sort((a, x) => a - x);
        const median = gaps.length ? gaps[Math.floor(gaps.length / 2)] : Infinity;
        const limit = isEditableTarget(e.target) ? MEDIAN_GAP_EDITABLE_MS : MEDIAN_GAP_FREE_MS;
        const looksLikeScan = b.chars.length >= SCAN_MIN_LEN && median <= limit;
        const code = b.chars;
        b.chars = ""; b.gaps = [];
        if (looksLikeScan) deliver(code);
        return;
      }
      if (e.key.length === 1) b.chars += e.key;
      else if (e.key !== "Shift") { b.chars = ""; b.gaps = []; }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [nav]);

  return (
    <HidCtx.Provider value={{ scannerConnected, subscribe }}>
      {children}
    </HidCtx.Provider>
  );
}

/** Subscribe a page to global HID scans. Pass a stable or ref-wrapped callback. */
export function useHidScanner(onScan) {
  const { scannerConnected, subscribe } = useContext(HidCtx);
  const cbRef = useRef(onScan);
  cbRef.current = onScan;
  useEffect(() => {
    if (!onScan) return undefined;
    return subscribe((code) => cbRef.current && cbRef.current(code));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, !!onScan]);
  return { scannerConnected };
}
